import { useId, useRef, useState } from 'react';
import pluralize from 'pluralize';
import {
  Box,
  Center,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  VisuallyHidden,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router';
import TextInput from 'src/components/design-system/input/TextInput';
import Textarea from 'src/components/design-system/input/Textarea';
import { Button } from 'src/design-system/Button';
import { CheckSpeakersModal } from 'src/meetings/components/CheckSpeakersModal';
import {
  ChooseSpeakerModal,
  type SelectedSpeaker,
} from 'src/meetings/components/ChooseSpeakerModal';
import { SpeakerConflictModal } from 'src/meetings/components/SpeakerConflictModal';
import { useEvent } from 'src/meetings/hooks/useEvent';
import type { CalendarEvent, Participant } from 'src/meetings/models';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import {
  type AssignedSpeaker,
  type AssignSpeakerBody,
  type SpeakerConflict,
  speakerConflictOf,
  useAssignSpeaker,
} from 'src/meetings/hooks/useAssignSpeaker';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  type SpeakerSuggestion,
  TranscriptionStatus,
} from 'src/meetings/detail/tabs/transcript/types';
import { TranscriptSegment } from 'src/meetings/detail/tabs/transcript/TranscriptSegment';
import {
  assignSpeakerPath,
  meetingDetailPath,
  meetingScopeFromParams,
  type MeetingScope,
} from 'src/meetings/scope';
import { getClientParticipantIds } from 'src/meetings/utils/event-utils';
import { useClients } from 'src/clients/hooks/useClients';
import {
  getSpeakers,
  type MeetingSpeaker,
  type SpeakerStatus,
} from 'src/meetings/utils/speaker-utils';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const DESCRIPTION_MAX_LENGTH = 1000;

type SpeakerForm = {
  name: string;
  description: string;
  selected: SelectedSpeaker | null;
};

const EMPTY_FORM: SpeakerForm = { name: '', description: '', selected: null };

// The meeting screen only shows a meeting under a client or family while one of their clients
// attends it, and changing a speaker can take a client off the meeting. `familyClientIds` is
// undefined while the family's clients are still loading, which counts as still attending.
function isMeetingInScope(
  scope: MeetingScope,
  event: CalendarEvent | null | undefined,
  familyClientIds: string[] | undefined,
): boolean {
  if (!event) return true;
  const attendeeClientIds = getClientParticipantIds(event);
  switch (scope.kind) {
    case 'all':
      return true;
    case 'individual':
      return attendeeClientIds.includes(scope.clientId);
    case 'family':
      return (
        familyClientIds === undefined ||
        attendeeClientIds.some(attendeeClientId => familyClientIds.includes(attendeeClientId))
      );
  }
}

// A suggestion naming a client or colleague links their record; any other name is typed in.
function withSuggestion(form: SpeakerForm, suggestion: SpeakerSuggestion): SpeakerForm {
  const { name, clientId, userId } = suggestion;
  if (clientId) return { ...form, selected: { kind: 'client', id: clientId, name } };
  if (userId) return { ...form, selected: { kind: 'user', id: userId, name } };
  return { ...form, name, selected: null };
}

// The line under a pre-filled suggestion: where it came from, and the name the meeting calls the voice if another.
function suggestionHint(suggestion: SpeakerSuggestion, splitOffFromThem: boolean): string {
  const { name, heardAs } = suggestion;
  const heard = heardAs ? `, but the meeting calls this speaker “${heardAs}”` : '';
  if (splitOffFromThem) {
    return `Suggested by Siesta: probably part of what ${name} said, split off by the recording${heard}.`;
  }
  return `Suggested by Siesta${heard}. Check it’s right before you identify them.`;
}

function isApplied(form: SpeakerForm, suggestion: SpeakerSuggestion): boolean {
  const { selected } = form;
  if (suggestion.clientId) {
    return selected?.kind === 'client' && selected.id === suggestion.clientId;
  }
  if (suggestion.userId) {
    return selected?.kind === 'user' && selected.id === suggestion.userId;
  }
  return selected === null && form.name === suggestion.name;
}

// Naming an external attendee already on the meeting who has a description links them as they are.
function describedAttendee(
  event: CalendarEvent | null | undefined,
  name: string,
): Participant | undefined {
  const wanted = name.trim().toLocaleLowerCase();
  if (!wanted) return undefined;
  return (event?.eventParticipants ?? [])
    .map(eventParticipant => eventParticipant.participant)
    .find(
      participant =>
        participant.participantType === 'EXTERNAL' &&
        participant.name?.trim().toLocaleLowerCase() === wanted &&
        Boolean(participant.description?.trim()),
    );
}

// Returns the label to activate after assigning `assignedLabel`: the next
// unidentified speaker in appearance order, falling back to the previous one
// when the last was assigned, or null when none remain.
function getNextSpeakerLabel(
  speakers: MeetingSpeaker[],
  assignedLabel: string,
  handled: ReadonlySet<string>,
): string | null {
  // `speakers` is derived from the transcript, which has not refetched yet, so everyone just
  // assigned is still listed as needing attention. `handled` carries the voices dealt with in
  // this visit, without which a quick run through several speakers lands back on a finished
  // one instead of finishing.
  const outstanding = (speaker: MeetingSpeaker) =>
    speaker.label !== assignedLabel && !speaker.correctionLabels.every(label => handled.has(label));
  if (!speakers.some(outstanding)) return null;
  const currentIndex = speakers.findIndex(speaker => speaker.label === assignedLabel);
  const after = speakers.slice(currentIndex + 1).find(outstanding);
  const before = [...speakers.slice(0, Math.max(currentIndex, 0))].reverse().find(outstanding);
  return (after ?? before)?.label ?? null;
}

const STATUS_LABELS: Record<SpeakerStatus, string> = {
  unidentified: 'Not identified',
  confirmed: 'Confirmed',
};

function SpeakerStatusMarker({ status }: { status: SpeakerStatus }) {
  if (status === 'confirmed') {
    return (
      <IconCheck
        size={14}
        color="var(--mantine-color-gray-6)"
        style={{ flexShrink: 0 }}
        aria-hidden
      />
    );
  }
  // Speakers still needing a name carry a solid brand dot.
  return (
    <Box w={8} h={8} bg="brand.6" aria-hidden style={{ borderRadius: '50%', flexShrink: 0 }} />
  );
}

function SpeakerTab({
  speaker,
  status,
  isActive,
  onClick,
}: {
  speaker: MeetingSpeaker;
  status: SpeakerStatus;
  isActive: boolean;
  onClick: () => void;
}) {
  const activeColour = status === 'confirmed' ? 'gray.10' : 'brand.8';
  // A filled dot and a tick are the only thing separating the two states, which is nothing at
  // all to a screen reader. The status rides as a description rather than in the
  // button's name, so the tab is still announced (and still found) by the speaker's name.
  const statusId = useId();
  return (
    <>
      <UnstyledButton
        onClick={onClick}
        aria-pressed={isActive}
        aria-describedby={statusId}
        bg={isActive ? 'brand.1' : 'white'}
        py="xxs"
        px="xs"
        style={{ borderRadius: 6 }}
      >
        <Flex align="center" gap="xxs">
          <SpeakerStatusMarker status={status} />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c={isActive ? activeColour : 'gray.6'}>
            {speaker.displayName}
          </Text>
        </Flex>
      </UnstyledButton>
      <VisuallyHidden id={statusId}>{STATUS_LABELS[status]}</VisuallyHidden>
    </>
  );
}

export default function AssignSpeakerPage() {
  const {
    familyId,
    clientId,
    eventId = '',
    speakerLabel = '',
  } = useParams<{
    familyId?: string;
    clientId?: string;
    eventId: string;
    speakerLabel: string;
  }>();
  const navigate = useNavigate();
  const scope: MeetingScope = meetingScopeFromParams({ familyId, clientId });

  const { event } = useEvent(eventId);
  const { clients: familyClients, isLoading: isFamilyClientsLoading } = useClients(
    { familyId },
    { enabled: !!familyId },
  );
  const { transcript, isLoading } = useTranscript({ eventId });
  const assignSpeaker = useAssignSpeaker(scope, eventId);
  const { flagEnabledSpeakerPipelineRefactor } = useFlags();

  const meetingTitle = event?.title;
  const pageTitle = meetingTitle ? `Speakers for ${meetingTitle}` : 'Speakers';

  // The URL names the speaker on show. Replace rather than push, so Back still leaves the page
  // instead of stepping back through every speaker visited.
  const showSpeaker = (label: string) =>
    navigate(assignSpeakerPath(scope, eventId, label), { replace: true });
  // A display name is not a stable handle on a speaker: assigning one rewrites it. The
  // diarisation label survives every relabel, so once we have acted on a speaker we follow them
  // by voice.
  const [activeVoice, setActiveVoice] = useState<string | null>(null);
  // Voices assigned during this visit. A ref, because the next-speaker decision is made in the
  // same handler that records one, before any re-render.
  const handledVoices = useRef<Set<string>>(new Set());
  const [formByLabel, setFormByLabel] = useState<Record<string, SpeakerForm>>({});
  const [editingLabels, setEditingLabels] = useState<Record<string, boolean>>({});
  const [chooseClientOpen, setChooseClientOpen] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  // The speaker the backend said the pending assignment would collide with, while the adviser
  // decides what to do about it.
  const [conflict, setConflict] = useState<SpeakerConflict | null>(null);

  const familyClientIds = isFamilyClientsLoading
    ? undefined
    : familyClients.map(familyClient => familyClient.id);
  const meetingPathFor = (current: CalendarEvent | null | undefined) =>
    meetingDetailPath(
      isMeetingInScope(scope, current, familyClientIds) ? scope : { kind: 'all' },
      eventId,
    );
  const meetingPath = meetingPathFor(event);

  if (isLoading && !transcript) {
    return (
      <Center py="xxl">
        <Loader />
      </Center>
    );
  }

  if (transcript?.status !== TranscriptionStatus.READY) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Text c="gray.6">No transcript is available for this meeting.</Text>
      </Box>
    );
  }

  const speakers = getSpeakers(transcript.segments, transcript.speakerFragments);

  if (speakers.length === 0) {
    return (
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
            {pageTitle}
          </Text>
          <Button variant="text" onClick={() => navigate(meetingPath)}>
            Back to meeting
          </Button>
        </Flex>
        <Text mt="lg" size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6">
          No speakers were detected in this meeting.
        </Text>
      </Box>
    );
  }

  const reviewSpeakers = speakers.filter(
    speaker => !speaker.isFragment && speaker.status !== 'confirmed',
  );
  const toReviewCount = reviewSpeakers.length;
  const speakerWord = pluralize('speaker', speakers.length);
  const subtitle =
    toReviewCount > 0
      ? `${speakers.length} ${speakerWord} · ${toReviewCount} to identify`
      : `${speakers.length} ${speakerWord} · all confirmed`;

  const activeSpeaker =
    (activeVoice !== null
      ? speakers.find(speaker => speaker.correctionLabels.includes(activeVoice))
      : undefined) ??
    speakers.find(speaker => speaker.label === speakerLabel) ??
    reviewSpeakers[0] ??
    speakers[0];
  const currentLabel = activeSpeaker.label;
  // Suggestions are keyed by diarisation label and one speaker here can cover
  // several, so take the first label Siesta has a suggestion for. Only an
  // unidentified speaker is pre-filled; reassigning starts from a blank form.
  const suggestion =
    activeSpeaker.status === 'unidentified'
      ? activeSpeaker.correctionLabels
          .map(label => transcript.speakerSuggestions?.[label])
          .find(entry => entry !== undefined)
      : undefined;
  const initialForm = suggestion ? withSuggestion(EMPTY_FORM, suggestion) : EMPTY_FORM;
  const form = formByLabel[currentLabel] ?? initialForm;

  const updateForm = (patch: Partial<SpeakerForm>) =>
    setFormByLabel(previous => ({
      ...previous,
      [currentLabel]: { ...(previous[currentLabel] ?? initialForm), ...patch },
    }));

  const speakerSegments =
    transcript.segments?.filter(segment => segment.speaker === currentLabel) ?? [];

  const activeStatus = activeSpeaker.status;
  const isEditing = editingLabels[currentLabel] ?? false;
  // "Change" edits an already-named speaker (identified or confirmed) via the same form.
  const isReassigning = activeStatus !== 'unidentified' && isEditing;
  const showForm = activeStatus === 'unidentified' || isReassigning;

  const canReuseAttendee = flagEnabledSpeakerPipelineRefactor && form.selected === null;
  const attendee = canReuseAttendee ? describedAttendee(event, form.name) : undefined;
  const canAssign = form.selected
    ? true
    : form.name.trim().length > 0 && (attendee !== undefined || form.description.trim().length > 0);

  const isShowingSuggestion = suggestion !== undefined && isApplied(form, suggestion);

  const selectedSpeakerName = form.selected?.name ?? null;

  const startReassigning = () =>
    setEditingLabels(previous => ({ ...previous, [currentLabel]: true }));

  const forgetAnswerFor = (label: string) =>
    setFormByLabel(previous => {
      const next = { ...previous };
      delete next[label];
      return next;
    });

  const stopReassigning = () => {
    setEditingLabels(previous => ({ ...previous, [currentLabel]: false }));
    forgetAnswerFor(currentLabel);
  };

  function buildParticipant(): AssignSpeakerBody['participant'] {
    const selected = form.selected;
    if (selected?.kind === 'client') return { participantType: 'CLIENT', clientId: selected.id };
    if (selected?.kind === 'user') return { participantType: 'USER', userId: selected.id };
    if (selected?.kind === 'keyContact')
      return {
        participantType: 'EXTERNAL',
        name: selected.name,
        description: selected.description,
      };
    return {
      participantType: 'EXTERNAL',
      name: form.name.trim(),
      description: attendee?.description?.trim() ?? form.description.trim(),
    };
  }

  const pendingOnConflict = assignSpeaker.isPending
    ? assignSpeaker.variables?.onConflict
    : undefined;
  const pendingResolution =
    pendingOnConflict === 'swap' || pendingOnConflict === 'merge' ? pendingOnConflict : null;

  // Assigning relabels this speaker's segments to the chosen person's name, so a speaker already
  // shown under that name would be swept up with them. The first attempt asks the backend to
  // assign only if nobody would be, and to say who instead, so the adviser can choose. Siesta
  // suggests someone who already speaks here only for a voice split off from them, so merging is
  // the likely choice when confirming that suggestion is what collides.
  const confirmsSplitOff =
    isShowingSuggestion && form.selected !== null && suggestion?.alreadySpeaks === true;
  const hint = suggestion ? suggestionHint(suggestion, confirmsSplitOff) : undefined;

  const handleAssign = () => performAssign('ask');

  async function performAssign(onConflict: AssignSpeakerBody['onConflict']) {
    // One speaker on this screen can cover several diarisation labels (e.g. two
    // unknown speakers identified as the same person), so correct every one.
    const correctionLabels = activeSpeaker.correctionLabels;
    const wasReassigning = isReassigning;
    // Swapping from a voice nobody has named gives the far side no name back, so it returns to
    // needing one. `speakers` predates the refetch and still shows it named, so advancing from
    // here would walk straight past it — and off the page if it was the last left to review.
    const displacedVoices =
      onConflict === 'swap' && activeStatus === 'unidentified'
        ? (conflict?.speakerLabels ?? [])
        : [];
    const participant = buildParticipant();
    let assigned: AssignedSpeaker;
    try {
      const response = await assignSpeaker.mutateAsync({
        speakerLabels: correctionLabels,
        participant,
        onConflict,
      });
      assigned = response.data;
    } catch (error) {
      const raised = speakerConflictOf(error);
      if (raised) {
        setConflict(raised);
        return;
      }
      notifyAndLogError(
        'Failed to identify speaker',
        'Could not identify this speaker. Please try again.',
        error,
      );
      // Leave the question open on a failure: nothing was decided, so the adviser can retry or
      // back out rather than be dropped back onto the form with no explanation.
      return;
    }
    setConflict(null);

    // Reassigning an already-named speaker updates them in place. Keep the tab on that
    // speaker by their voice — the name shown against them is about to change.
    if (wasReassigning) {
      stopReassigning();
      // The voice is the reliable handle, but on transcripts predating diarisation capture it
      // is only the old display name, which the relabel has just invalidated. Keep the name the
      // backend wrote too so those transcripts still land on the right tab.
      setActiveVoice(correctionLabels[0] ?? null);
      showSpeaker(assigned.speakerName);
      return;
    }

    // Go to the voice the swap just unnamed rather than past it. It is no longer dealt with,
    // even if the adviser named it earlier in this visit.
    const displacedVoice = displacedVoices[0];
    if (displacedVoice !== undefined) {
      for (const label of correctionLabels) handledVoices.current.add(label);
      for (const label of displacedVoices) handledVoices.current.delete(label);
      // The displaced voice takes on the label this one was showing, which is the key the
      // answer is held under. Left there it would meet the adviser pre-filled with the person
      // just moved off this voice, and confirming it would raise the same collision again.
      forgetAnswerFor(currentLabel);
      setActiveVoice(displacedVoice);
      return;
    }

    advanceOrFinish(correctionLabels, meetingPathFor(assigned));
  }

  // After confirming/identifying a speaker, move to the next one still needing review, or
  // finish once none remain. `handled` are the voices just dealt with: the transcript has not
  // refetched, so they still look outstanding in `reviewSpeakers`.
  function advanceOrFinish(handled: string[], finishPath = meetingPath) {
    for (const label of handled) handledVoices.current.add(label);

    const nextLabel = getNextSpeakerLabel(reviewSpeakers, currentLabel, handledVoices.current);
    if (nextLabel !== null) {
      setActiveVoice(null);
      showSpeaker(nextLabel);
      return;
    }
    navigate(finishPath);
  }

  function handleBackToMeeting() {
    if (toReviewCount > 0) {
      setShowCheckModal(true);
    } else {
      navigate(meetingPath);
    }
  }

  return (
    <>
      <Box pt={36} px="xxl" pb="xxl">
        <Flex align="center" justify="space-between" gap="sm">
          <Stack gap={4}>
            <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="gray.10">
              {pageTitle}
            </Text>
            <Text size="textSm" c="gray.6">
              {subtitle}
            </Text>
          </Stack>
          <Button variant="text" onClick={handleBackToMeeting}>
            Back to meeting
          </Button>
        </Flex>

        {speakers.length > 1 && (
          <Flex mt="lg" gap="xxxs" wrap="wrap">
            {speakers.map(speaker => (
              <SpeakerTab
                key={speaker.label}
                speaker={speaker}
                status={speaker.status}
                isActive={speaker.label === currentLabel}
                onClick={() => {
                  setActiveVoice(null);
                  showSpeaker(speaker.label);
                }}
              />
            ))}
          </Flex>
        )}

        <Stack
          mt="lg"
          gap="md"
          p="lg"
          style={{ border: '1px solid var(--mantine-color-gray-1)', borderRadius: 20 }}
        >
          {showForm && form.selected && (
            <>
              <Stack gap={4}>
                <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                  Is this {selectedSpeakerName}?
                </Text>
                <Text size="textSm" c="gray.6">
                  {isShowingSuggestion
                    ? hint
                    : 'Check this is the right person before you confirm them.'}
                </Text>
              </Stack>

              <Flex justify="flex-end" align="center" gap="sm">
                {isReassigning && (
                  <Button variant="text" size="sm" onClick={stopReassigning}>
                    Keep {activeSpeaker.displayName}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => updateForm({ selected: null })}
                >
                  Change
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={assignSpeaker.isPending}
                  loading={assignSpeaker.isPending}
                  onClick={handleAssign}
                >
                  Confirm {selectedSpeakerName}
                </Button>
              </Flex>
            </>
          )}

          {showForm && !form.selected && (
            <>
              <Flex align="flex-start" justify="space-between" gap="md">
                <Stack gap={4}>
                  <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    {isReassigning
                      ? 'Who is this speaker?'
                      : `Who is ${activeSpeaker.displayName}?`}
                  </Text>
                  <Text size="textSm" c="gray.6">
                    Link them to a client, or add their name and who they are.
                  </Text>
                </Stack>
                <Button variant="secondary" size="sm" onClick={() => setChooseClientOpen(true)}>
                  Choose a speaker
                </Button>
              </Flex>

              <TextInput
                label="Name"
                size="md"
                placeholder="Enter speaker name"
                required
                value={form.name}
                onChange={event => updateForm({ name: event.currentTarget.value })}
                description={isShowingSuggestion ? hint : undefined}
                inputWrapperOrder={['label', 'input', 'description', 'error']}
                styles={{
                  input: { border: '1px solid var(--mantine-color-gray-3)' },
                }}
              />

              {attendee ? (
                <Text size="textSm" c="gray.6">
                  Already on this meeting: {attendee.description}
                </Text>
              ) : (
                <Textarea
                  label="Who they are"
                  size="md"
                  placeholder={'e.g. "David\'s wife" or "solicitor from Thornton & Co"'}
                  required
                  autosize
                  minRows={2}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  value={form.description}
                  onChange={event => updateForm({ description: event.currentTarget.value })}
                  description={`${form.description.length}/${DESCRIPTION_MAX_LENGTH}`}
                  inputWrapperOrder={['label', 'input', 'description', 'error']}
                  styles={{
                    input: { border: '1px solid var(--mantine-color-gray-3)' },
                    description: { textAlign: 'right' },
                  }}
                />
              )}

              {suggestion && !isShowingSuggestion && (
                <Group>
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => updateForm(withSuggestion(form, suggestion))}
                  >
                    Use Siesta’s suggestion: {suggestion.name}
                  </Button>
                </Group>
              )}

              <Flex justify="flex-end" align="center" gap="sm">
                {isReassigning && (
                  <Button variant="text" size="sm" onClick={stopReassigning}>
                    Keep {activeSpeaker.displayName}
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canAssign || assignSpeaker.isPending}
                  loading={assignSpeaker.isPending}
                  onClick={handleAssign}
                >
                  {isReassigning ? 'Save speaker' : 'Identify speaker'}
                </Button>
              </Flex>
            </>
          )}

          {!showForm && (
            <Stack gap="sm">
              <Flex align="center" justify="space-between" gap="sm">
                <Flex align="center" gap="sm">
                  <Text size="textLg" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    {activeSpeaker.displayName}
                  </Text>
                  <Flex
                    align="center"
                    gap="xxxs"
                    py={2}
                    px="xs"
                    bg="gray.0"
                    style={{ borderRadius: 20 }}
                  >
                    <SpeakerStatusMarker status={activeStatus} />
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.7">
                      Confirmed
                    </Text>
                  </Flex>
                </Flex>
                <Button variant="secondary" size="sm" onClick={startReassigning}>
                  Change
                </Button>
              </Flex>
            </Stack>
          )}
        </Stack>

        <Box mt="xl">
          <Group gap="xs" mb="sm" align="baseline">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              What they said
            </Text>
            <Text size="textSm" c="gray.6">
              {pluralize('statement', activeSpeaker.statementCount, true)}
            </Text>
          </Group>

          <Stack gap="xxs">
            {speakerSegments.map((segment, index) => (
              <TranscriptSegment
                key={`${segment.startTime}-${index}`}
                segment={segment}
                selected={false}
                hideSpeaker
              />
            ))}
          </Stack>
        </Box>
      </Box>

      {event && (
        <ChooseSpeakerModal
          opened={chooseClientOpen}
          onClose={() => setChooseClientOpen(false)}
          scope={scope}
          event={event}
          onSelect={selected => updateForm({ selected })}
        />
      )}

      {conflict && (
        <SpeakerConflictModal
          opened
          personName={conflict.personName}
          currentSpeakerName={activeStatus === 'unidentified' ? null : activeSpeaker.displayName}
          pending={pendingResolution}
          mergeIsLikely={confirmsSplitOff}
          onCancel={() => setConflict(null)}
          onSwap={() => performAssign('swap')}
          onMerge={() => performAssign('merge')}
        />
      )}

      <CheckSpeakersModal
        opened={showCheckModal}
        count={toReviewCount}
        onClose={() => setShowCheckModal(false)}
        onContinue={() => navigate(meetingPath)}
      />
    </>
  );
}
